<script setup lang="ts">
import type { ChartData } from "chart.js";

const { getBranchColor, getBranchLabel } = useBranches();

const props = defineProps<{
  chartType: "bar" | "line"
  dataField: "orders" | "revenue" | "newCustomers" | "avgBill" | "cumulativeCustomers"
  title: string
  icon: string
  dailyData: Array<{
    date: string
    branch: string
    orders: number
    revenue: number
    newCustomers: number
    avgBill: number
    cumulativeCustomers?: number
  }>
  granularity: 'daily' | 'weekly' | 'monthly'
  loading?: boolean
}>();

// Transform branch-based dailyData into Chart.js format
const chartData = computed<ChartData<'bar' | 'line'>>(() => {
  if (!props.dailyData || props.dailyData.length === 0) {
    return {
      labels: [],
      datasets: []
    };
  }

  // Get unique dates and branches from backend data
  const uniqueDates = [...new Set(props.dailyData.map(d => d.date))].sort();
  const uniqueBranches = [...new Set(props.dailyData.map(d => d.branch))].sort();

  // Get global dataset defaults based on chart type
  const baseDataset = props.chartType === "line"
    ? getLineDatasetDefaults("dashboard")
    : getBarDatasetDefaults();

  // Create datasets dynamically for each branch found in data
  const datasets = uniqueBranches.map(branchName => {
    const branchData = uniqueDates.map(date => {
      const item = props.dailyData.find(d => d.date === date && d.branch === branchName);
      return item?.[props.dataField] || (props.chartType === "line" ? null : 0);
    });

    const color = getBranchColor(branchName);
    const label = getBranchLabel(branchName);

    return {
      ...baseDataset,
      label: label,
      data: branchData,
      backgroundColor: color,
      borderColor: color,
      hidden: false,
      spanGaps: props.chartType === "line" ? true : undefined
    };
  });

  return {
    labels: uniqueDates,
    datasets
  };
});
</script>

<template>
  <AppCard :icon="icon" :title="title">
    <TimeSeriesChart
      :data="chartData"
      :granularity="granularity"
      :chart-type="chartType"
    />
  </AppCard>
</template>
